import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';
import { PRODUCTION_URL } from '../productionLink/productionLink';

const HouseKeepingSettings = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [settings, setSettings] = useState([]);
  const [editedSettings, setEditedSettings] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a',
    warning: '#f59e0b',
    danger: '#ef4444'
  };

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const data = await apiGet('/admin/housekeeping/settings');
      if (data.success) {
        setSettings(data.data);
        const initialSettings = {};
        data.data.forEach(setting => {
          initialSettings[setting.setting_key] = setting.setting_value;
        });
        setEditedSettings(initialSettings);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
    setLoading(false);
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();

    try {
      const updates = Object.keys(editedSettings).map(key =>
        fetch(`${PRODUCTION_URL}/api/admin/housekeeping/settings/${key}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ value: editedSettings[key] })
        })
      );

      await Promise.all(updates);
      setSuccessMessage('✅ Settings saved successfully!');
      fetchSettings();
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Error saving settings');
    }
  };

  const handleInputChange = (key, value) => {
    setEditedSettings({
      ...editedSettings,
      [key]: value
    });
  };

  const styles = {
    pageHeader: {
      marginBottom: isMobile ? '16px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    pageTitle: {
      fontSize: isMobile ? '20px' : isTablet ? '24px' : '28px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: '6px'
    },
    contentCard: {
      background: colors.white,
      padding: isMobile ? '20px' : '28px',
      borderRadius: isMobile ? '14px' : '16px',
      boxShadow: '0 8px 24px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`,
      margin: isMobile ? '0 4px' : '0'
    },
    sectionTitle: {
      fontSize: isMobile ? '16px' : '20px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: isMobile ? '16px' : '20px',
      paddingBottom: '12px',
      borderBottom: `2px solid ${colors.lightGray}`,
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    settingsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
      gap: isMobile ? '16px' : '20px',
      marginBottom: '24px'
    },
    settingItem: {
      padding: isMobile ? '16px' : '18px',
      background: colors.lightGray,
      borderRadius: '10px',
      border: `1px solid ${colors.mediumGray}`
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      fontSize: isMobile ? '12px' : '13px',
      fontWeight: '700',
      color: colors.textDark,
      textTransform: 'capitalize'
    },
    description: {
      fontSize: '11px',
      color: colors.textLight,
      marginBottom: '10px',
      fontStyle: 'italic'
    },
    input: {
      width: '100%',
      padding: isMobile ? '10px 12px' : '12px 14px',
      borderRadius: '8px',
      border: `1px solid ${colors.mediumGray}`,
      fontSize: isMobile ? '13px' : '14px',
      fontFamily: 'inherit',
      background: colors.white,
      transition: 'border-color 0.3s ease'
    },
    button: {
      padding: isMobile ? '12px 24px' : '14px 32px',
      borderRadius: '8px',
      border: 'none',
      fontSize: isMobile ? '13px' : '14px',
      fontWeight: '700',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: colors.primary,
      color: colors.white,
      width: isMobile ? '100%' : 'auto',
      boxShadow: '0 4px 12px rgba(45, 80, 22, 0.2)'
    },
    successMessage: {
      padding: isMobile ? '12px 16px' : '14px 20px',
      borderRadius: '10px',
      marginBottom: '20px',
      fontSize: isMobile ? '13px' : '14px',
      fontWeight: '600',
      background: 'rgba(74, 124, 42, 0.15)',
      color: colors.success,
      border: `2px solid ${colors.success}`,
      animation: 'slideIn 0.3s ease-out',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <div>Loading settings...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <style>{`
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        input:focus {
          outline: none;
          border-color: ${colors.primary};
          box-shadow: 0 0 0 3px ${colors.primary}20;
        }
      `}</style>

      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>Housekeeping Settings</h1>
        <p style={{ fontSize: isMobile ? '12px' : '14px', color: colors.textLight }}>
          {isMobile ? 'Configuration settings' : 'Configure housekeeping preferences, notifications, and operational settings'}
        </p>
      </div>

      {successMessage && (
        <div style={styles.successMessage}>
          <span style={{ fontSize: '18px' }}>✅</span>
          <span>{successMessage}</span>
        </div>
      )}

      <form onSubmit={handleSaveSettings}>
        <div style={styles.contentCard}>
          <h2 style={styles.sectionTitle}><span>⏱️</span> Cleaning Time Standards</h2>

          <div style={styles.settingsGrid}>
            {settings
              .filter(s => ['housekeeping_clean_time', 'housekeeping_deep_clean_time'].includes(s.setting_key))
              .map(setting => (
                <div key={setting.setting_key} style={styles.settingItem}>
                  <label style={styles.label}>
                    {setting.setting_key.replace('housekeeping_', '').replace(/_/g, ' ')}
                  </label>
                  <div style={styles.description}>{setting.description}</div>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="number"
                      value={editedSettings[setting.setting_key] || ''}
                      onChange={(e) => handleInputChange(setting.setting_key, e.target.value)}
                      style={styles.input}
                    />
                    <div style={{
                      position: 'absolute',
                      right: '14px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: colors.textLight,
                      fontSize: '13px',
                      fontWeight: '600'
                    }}>
                      min
                    </div>
                  </div>
                </div>
              ))}
          </div>

          <h2 style={styles.sectionTitle}><span>⏰</span> Work Schedule</h2>

          <div style={styles.settingsGrid}>
            {settings
              .filter(s => ['housekeeping_shift_start', 'housekeeping_shift_end'].includes(s.setting_key))
              .map(setting => (
                <div key={setting.setting_key} style={styles.settingItem}>
                  <label style={styles.label}>
                    {setting.setting_key.replace('housekeeping_', '').replace(/_/g, ' ')}
                  </label>
                  <div style={styles.description}>{setting.description}</div>
                  <input
                    type="time"
                    value={editedSettings[setting.setting_key] || ''}
                    onChange={(e) => handleInputChange(setting.setting_key, e.target.value)}
                    style={styles.input}
                  />
                </div>
              ))}
          </div>

          <h2 style={styles.sectionTitle}><span>✅</span> Quality Control</h2>

          <div style={styles.settingsGrid}>
            {settings
              .filter(s => s.setting_key === 'housekeeping_inspection_required')
              .map(setting => (
                <div key={setting.setting_key} style={styles.settingItem}>
                  <label style={styles.label}>
                    {setting.setting_key.replace('housekeeping_', '').replace(/_/g, ' ')}
                  </label>
                  <div style={styles.description}>{setting.description}</div>
                  <select
                    value={editedSettings[setting.setting_key] || 'true'}
                    onChange={(e) => handleInputChange(setting.setting_key, e.target.value)}
                    style={styles.input}
                  >
                    <option value="true">Yes, Required</option>
                    <option value="false">No, Optional</option>
                  </select>
                </div>
              ))}
          </div>

          <div style={{
            marginTop: '28px',
            paddingTop: '20px',
            borderTop: `2px solid ${colors.lightGray}`,
            display: 'flex',
            justifyContent: isMobile ? 'center' : 'flex-end',
            gap: '12px'
          }}>
            <button
              type="button"
              onClick={fetchSettings}
              style={{
                ...styles.button,
                background: colors.mediumGray,
                color: colors.textDark,
                boxShadow: 'none'
              }}
            >
              Reset
            </button>
            <button
              type="submit"
              style={styles.button}
              onMouseEnter={(e) => !isMobile && (e.currentTarget.style.transform = 'translateY(-2px)')}
              onMouseLeave={(e) => !isMobile && (e.currentTarget.style.transform = 'translateY(0)')}
            >
              💾 Save All Settings
            </button>
          </div>
        </div>
      </form>

      <div style={{
        margin: isMobile ? '16px 4px 0 4px' : '24px 0 0 0',
        padding: isMobile ? '16px' : '20px',
        background: `${colors.primary}10`,
        borderRadius: isMobile ? '12px' : '14px',
        border: `2px solid ${colors.primary}30`
      }}>
        <div style={{ fontSize: isMobile ? '13px' : '14px', color: colors.textDark, marginBottom: '8px' }}>
          <strong>ℹ️ Housekeeping Best Practices:</strong>
        </div>
        <ul style={{
          margin: 0,
          paddingLeft: '20px',
          fontSize: isMobile ? '12px' : '13px',
          color: colors.textLight,
          lineHeight: '1.6'
        }}>
          <li>Standard room cleaning typically takes 25-35 minutes</li>
          <li>Deep cleaning (checkout rooms) may take 45-75 minutes</li>
          <li>Inspection ensures quality standards are maintained</li>
          <li>Regular shift times help maintain consistency</li>
        </ul>
      </div>
    </AdminLayout>
  );
};

export default HouseKeepingSettings;